/**
 * Every manifest line /hldebug/provision-user accepts, for the reference page
 * beside it. The source of truth is the qa-climber CLI:
 * packages/cli-util/qa-climber/manifest/grammar.ts (the verbs and their keys)
 * and packages/cli-util/qa-climber/MANIFEST.md (what each one does). Keep this
 * in step with both when a verb is added or changes shape.
 *
 * Only the verbs qa-climber implements are listed; grammar.ts's report-only
 * verbs (qa-report-data) are left out.
 */

/** Where the ids a line takes come from. Each has a lookup on the page. */
export type IdSource =
  | "hatIndex"
  | "clusterPathways"
  | "connectorActivities"
  | "perksQuirks"
  | "badges"
  | "learning"
  | "awards"
  | "products"
  | "districtSettings";

export interface VerbArg {
  name: string;
  detail: string;
}

export interface VerbDoc {
  verb: string;
  shape: string;
  mode: "ms" | "wf" | "both";
  summary: string;
  args?: VerbArg[];
  notes?: string[];
  examples: string[];
  ids?: IdSource[];
}

export interface VerbGroup {
  id: string;
  title: string;
  intro?: string;
  verbs: VerbDoc[];
}

export const RATINGS =
  "LOVE, LIKE, NOT_SURE or DISLIKE (NOTFORME / NOT_FOR_ME are read as DISLIKE)";

export const MANIFEST_GROUPS: VerbGroup[] = [
  {
    id: "directives",
    title: "Directives",
    intro:
      "File-level settings. They do nothing to a climber and don't count as action lines.",
    verbs: [
      {
        verb: "mode",
        shape: "mode ms|wf",
        mode: "both",
        summary:
          "Which product family the file targets: ms (Possibilities) or wf (workforce).",
        examples: ["mode ms"],
      },
      {
        verb: "product",
        shape: "product <code>",
        mode: "both",
        summary:
          "The product code every write lands under. It must be a product the climber's class is subscribed to.",
        examples: ["product ms-base"],
        ids: ["products"],
      },
    ],
  },
  {
    id: "profile",
    title: "Profile",
    verbs: [
      {
        verb: "onboarding",
        shape: "onboarding careerStage=N careerConfidence=N topThemes=a,b,c",
        mode: "ms",
        summary:
          "Completes profile onboarding and emits profile-onboard-completed with +15 XP. Earns Jumpstarted.",
        args: [
          { name: "careerStage", detail: "0–4" },
          { name: "careerConfidence", detail: "0–3" },
          {
            name: "topThemes",
            detail: "Comma list of zero-padded RIASEC theme ids",
          },
        ],
        examples: [
          "onboarding careerStage=2 careerConfidence=1 topThemes=001,003,005",
        ],
      },
      {
        verb: "core-values",
        shape: "core-values grade=N",
        mode: "ms",
        summary:
          "Completes the RIASEC profiler. The ladder and climb follow the grade: 6 → 160/326, 7 → 3519/2240, 8 → 3520/2273. A missing or unknown grade falls back to 6.",
        notes: [
          "Scores come from the facet lines with the same ladder id. Without them the profiler completes with no scores.",
        ],
        examples: ["core-values grade=7"],
      },
      {
        verb: "work-values",
        shape: "work-values",
        mode: "ms",
        summary:
          "Completes the work-values profiler (ladder 848, climb 327). Scores come from facet 848 lines.",
        examples: ["work-values"],
      },
      {
        verb: "facet",
        shape: "facet <ladderId> <earned>/<max> <key…>",
        mode: "ms",
        summary:
          "One facet score for the core-values or work-values line with the same ladder id. Not an action on its own.",
        args: [
          {
            name: "ladderId",
            detail:
              "160, 3519 or 3520 (matching core-values grade=), or 848 for work values",
          },
          { name: "earned/max", detail: "The raw score, e.g. 40/48" },
          {
            name: "key",
            detail:
              "RIASEC:R … RIASEC:C for core values, or a work-value name (may contain spaces) for 848",
          },
        ],
        notes: [
          "The ladder id must belong to a core-values or work-values line in the same file, or the file is rejected. A grade=7 block takes facet 3519 lines, not 160.",
        ],
        examples: [
          "core-values grade=7\nfacet 3519 40/48 RIASEC:R\nfacet 3519 12/48 RIASEC:I",
          "work-values\nfacet 848 30/36 Being Independent",
        ],
      },
    ],
  },
  {
    id: "clusters",
    title: "Clusters and pathways",
    verbs: [
      {
        verb: "cluster-content",
        shape: "cluster-content <clusterId> <contentId>",
        mode: "ms",
        summary:
          "Completes one content block inside a cluster. A ladder block also records ladder progress and XP; an activity block also emits activity-completion.",
        notes: ["Leave some out for a partially explored cluster."],
        examples: ["cluster-content 15 3301"],
        ids: ["clusterPathways"],
      },
      {
        verb: "cluster-done",
        shape: "cluster-done <clusterId>",
        mode: "ms",
        summary:
          "Marks the cluster complete and earns its exploration badge (cluster 15 earns Roots Uncovered). Put it after the cluster's content lines.",
        examples: ["cluster-done 15"],
        ids: ["clusterPathways"],
      },
      {
        verb: "pathway-content",
        shape: "pathway-content <pathwayId> <contentId>",
        mode: "ms",
        summary:
          "Completes one content block inside a pathway and grants the pathway content-type XP.",
        examples: ["pathway-content 28 3410"],
        ids: ["clusterPathways"],
      },
      {
        verb: "pathway-rate",
        shape: "pathway-rate <pathwayId> cluster=<clusterId> rating=<R>",
        mode: "ms",
        summary: "Records a pathway sentiment and emits pathway-rated.",
        args: [
          { name: "cluster", detail: "Required. The pathway's cluster id" },
          { name: "rating", detail: RATINGS },
        ],
        examples: ["pathway-rate 28 cluster=15 rating=LOVE"],
        ids: ["clusterPathways"],
      },
      {
        verb: "pathway-prefer",
        shape: "pathway-prefer <pathwayId> score=<0-15>",
        mode: "ms",
        summary:
          "Sets a pathway preference score, which ranks the hat finder's Most Recommended sort. Separate from pathway-rate.",
        args: [
          {
            name: "score",
            detail:
              "Required, 0–15. 13.5 is highly recommended, 9 recommended, 1.5 not recommended",
          },
        ],
        notes: [
          "Idempotent: applying the same score twice writes nothing the second time. score=0 on a pathway with no entry writes nothing.",
        ],
        examples: ["pathway-prefer 28 score=13.5"],
        ids: ["clusterPathways"],
      },
    ],
  },
  {
    id: "hats",
    title: "Hats",
    verbs: [
      {
        verb: "hat",
        shape: "hat <hatId> [heardOf=yes|no] [rating=<R>]",
        mode: "ms",
        summary:
          "Runs the full hat interaction: heard-of, explored and rated, with the matching hat-record writes.",
        args: [
          {
            name: "heardOf",
            detail: "yes (default, +1 XP the first time) or no",
          },
          { name: "rating", detail: `${RATINGS}. Default is random` },
        ],
        examples: ["hat 1708", "hat 1923 heardOf=no rating=LIKE"],
        ids: ["hatIndex"],
      },
      {
        verb: "set-top-hat",
        shape: "set-top-hat <hatId> [<hatId>] [<hatId>]",
        mode: "ms",
        summary:
          "Fills the Top Hat slots (up to 3). Three hats earns Top Hat Trick. The first one is the default hat for a climb plan.",
        examples: ["set-top-hat 1923 1994 3252"],
        ids: ["hatIndex"],
      },
      {
        verb: "set-bottom-hat",
        shape: "set-bottom-hat <hatId> [<hatId>] [<hatId>]",
        mode: "ms",
        summary:
          "Fills the Bottom Hat slots (up to 3). Three hats earns Bottom Hat Trick.",
        examples: ["set-bottom-hat 1708 2136 2201"],
        ids: ["hatIndex"],
      },
      {
        verb: "like-perkquirk",
        shape: "like-perkquirk badge=00NN count=N [pq=<id>,<id>] [hat=<hatId>]",
        mode: "ms",
        summary:
          "Emits count perk-quirk likes tagged with a personality badge, which earns that badge.",
        args: [
          {
            name: "badge",
            detail: "Required. Zero-padded badge id, e.g. 0012",
          },
          { name: "count", detail: "How many likes to emit" },
          {
            name: "pq",
            detail:
              "The perks/quirks that were liked, cycled through. Without it the ids come from the global pool",
          },
          { name: "hat", detail: "The hat those perks/quirks sit on" },
        ],
        notes: [
          "Pass pq= and hat= together when the badge detail screen should explain the badge with perks the climber actually saw.",
        ],
        examples: [
          "like-perkquirk badge=0012 count=5",
          "like-perkquirk badge=0012 count=5 pq=1070,1071 hat=1708",
        ],
        ids: ["badges", "perksQuirks", "hatIndex"],
      },
    ],
  },
  {
    id: "connectors",
    title: "Building blocks",
    verbs: [
      {
        verb: "building-block",
        shape: 'building-block activity=<id> description="…" duration="…"',
        mode: "ms",
        summary:
          "Logs one building block against an authored connector activity, emits add-building-block (+10 XP), and rates 1–2 of its connectors LIKE and 1–2 DISLIKE.",
        args: [
          {
            name: "activity",
            detail:
              "Required. A connector activity id. Category and title follow from it",
          },
          {
            name: "description",
            detail: "Required. 10–100 characters, in double quotes",
          },
          {
            name: "duration",
            detail:
              'Required. One of "< 1 year", "1 - 2 years", "2 - 3 years", "3 - 5 years", "> 5 years"',
          },
        ],
        notes: [
          "There is no name= or category=; both come from the activity. Applying the same line twice makes two blocks.",
        ],
        examples: [
          'building-block activity=89 description="Built a line-following robot with my team" duration="1 - 2 years"',
        ],
        ids: ["connectorActivities"],
      },
    ],
  },
  {
    id: "learning",
    title: "Ladders, climbs, courses and awards",
    verbs: [
      {
        verb: "ladder",
        shape: "ladder <ladderId> [score=<earned>/<max>]",
        mode: "both",
        summary:
          "Completes one ladder with a plausible passing score and awards its XP. A repeated ladder id in one file is skipped.",
        args: [{ name: "score", detail: "Force an exact score, e.g. 9/10" }],
        examples: ["ladder 4120", "ladder 4121 score=9/10"],
        ids: ["learning"],
      },
      {
        verb: "climb",
        shape: "climb <climbId>",
        mode: "both",
        summary:
          "Completes a career climb and emits climb-completion. No XP; its ladders carry that.",
        examples: ["climb 326"],
        ids: ["learning"],
      },
      {
        verb: "course",
        shape: "course <courseId>",
        mode: "wf",
        summary:
          "Records a course completion. Put it after the course's climb lines.",
        examples: ["course 52"],
        ids: ["learning"],
      },
      {
        verb: "certificate",
        shape: "certificate <awardId> [type=certificate|pok]",
        mode: "wf",
        summary:
          "Records an award completion. The type is read from published content. Re-applying the same award is rejected as a duplicate.",
        examples: ["certificate 17"],
        ids: ["awards"],
      },
      {
        verb: "xp-topup",
        shape: "xp-topup <amount>",
        mode: "both",
        summary:
          "Tops the climber's XP up to a total, so the high XP-milestone badges can be earned. Does nothing if they are already there.",
        examples: ["xp-topup 5000"],
      },
    ],
  },
  {
    id: "climb-plan",
    title: "Climb Plans",
    intro:
      "A climb-plan line moves a plan to a status. The plan-* lines set its sections, and each one edits the current plan (the one most recently started). Every key on a plan-* line is optional: a key replaces only its part of the section, and anything still empty is filled with the first option the app would offer when a status needs it. So climb-plan submitted on its own builds a whole submitted plan. An id the app would not offer fails the line, and the error lists the ids it would offer.",
    verbs: [
      {
        verb: "climb-plan",
        shape: 'climb-plan <state> [hat=<hatId>] [comment="…"] [coach="…"]',
        mode: "ms",
        summary: "Moves a Climb Plan to a status.",
        args: [
          {
            name: "drafted",
            detail:
              "Always starts a new DRAFT plan with empty sections. hat= defaults to the first hat of the latest set-top-hat",
          },
          {
            name: "draft-complete",
            detail: "Fills empty sections with defaults, then DRAFT_COMPLETE",
          },
          {
            name: "submitted",
            detail:
              "Fills, signs and submits. After revisions-requested it is the resubmission",
          },
          {
            name: "revisions-requested",
            detail:
              'A coach sends it back. comment= is the feedback, coach= the name on it (default "QA Coach")',
          },
          {
            name: "approved",
            detail:
              "A coach approves it. Takes comment= and coach= the same way",
          },
          {
            name: "deleted",
            detail:
              "Deletes the current plan. An approved plan can't be deleted",
          },
        ],
        notes: [
          "Sections are editable only while the plan is DRAFT, DRAFT_COMPLETE or REVISIONS_REQUESTED. A coach can decide only a SUBMITTED plan. A climber can have only one plan submitted or approved at a time.",
          "coach= doesn't need to be a real account; it is only the name on the log entry.",
        ],
        examples: [
          "set-top-hat 1923 1994 3252\nclimb-plan submitted",
          'climb-plan drafted hat=1923\nclimb-plan submitted\nclimb-plan revisions-requested comment="Which club could you join?" coach="Ms. Rivera"\nplan-high-school goal=2878 goal-reflection="I could join the Environmental Club"\nclimb-plan submitted\nclimb-plan approved comment="Great plan!" coach="Ms. Rivera"',
        ],
        ids: ["hatIndex"],
      },
      {
        verb: "plan-profile",
        shape:
          'plan-profile [source=blocks|core-values] [blocks=<activityId>,…] [reflection="…"]',
        mode: "ms",
        summary:
          "Sets Connect Your Profile. The default uses the first three building-block lines in the file, or else the hat's six Core types scored against this file's core-values facets.",
        args: [
          { name: "source", detail: "blocks or core-values" },
          {
            name: "blocks",
            detail:
              "The activity= ids of building-block lines earlier in this manifest",
          },
          { name: "reflection", detail: "Free text, in double quotes" },
        ],
        examples: [
          'plan-profile source=blocks blocks=89,100 reflection="I like building things"',
        ],
        ids: ["connectorActivities"],
      },
      {
        verb: "plan-high-school",
        shape:
          'plan-high-school [program=<id>] [school=<id>] [goal=<keyId>] [goal-reflection="…"]',
        mode: "ms",
        summary:
          "Sets High School Priorities. The default is the district's best-matching program, its first high school that feeds from the climber's middle school, and the hat's first Key to Success.",
        args: [
          {
            name: "program",
            detail: "A district program id that matches the hat",
          },
          {
            name: "school",
            detail: "A high school id that offers that program",
          },
          { name: "goal", detail: "One of the hat's Keys to Success" },
          { name: "goal-reflection", detail: "Free text, in double quotes" },
        ],
        notes: [
          "With no published district settings, or no program matching the hat, the section stores NO_DISTRICT_SETTINGS or NO_MATCH_FOR_HAT, as the app does. Choose the hat to get that outcome; there is no key for it.",
          "Changing program= keeps the school if the new program is offered there.",
          "Only active programs in the district's published settings count. A program added in the CMS since the last publish, or still in draft, is rejected.",
          "A school is offered only if it feeds from the climber's middle school (when any feeder is configured), so only some of a program's schools may be valid for a given climber.",
        ],
        examples: [
          "plan-high-school program=41 school=14",
          'plan-high-school goal=2877 goal-reflection="I want to volunteer at the nature centre"',
        ],
        ids: ["districtSettings", "hatIndex"],
      },
      {
        verb: "plan-skills",
        shape:
          'plan-skills [best=<powerSkillId>] [improve=<powerSkillId>] [excited=<proSkillId>] [reflection="…"]',
        mode: "ms",
        summary:
          "Sets Skills to Build. The default is the hat's first two PowerSkills and its first ProSkill.",
        args: [
          { name: "best", detail: "One of the hat's PowerSkills" },
          {
            name: "improve",
            detail: "Another of the hat's PowerSkills (must differ from best)",
          },
          { name: "excited", detail: "One of the hat's ProSkills" },
          { name: "reflection", detail: "Free text, in double quotes" },
        ],
        examples: ["plan-skills best=279 improve=643 excited=2730"],
        ids: ["hatIndex"],
      },
      {
        verb: "plan-after",
        shape: "plan-after [paths=<pathId>,…]",
        mode: "ms",
        summary:
          "Sets After High School. The default is the hat's first path: popular paths first, then other routes.",
        args: [
          {
            name: "paths",
            detail: "Comma list of the hat's after-high-school path ids",
          },
        ],
        examples: ["plan-after paths=34"],
        ids: ["hatIndex"],
      },
      {
        verb: "plan-sign",
        shape: 'plan-sign [reflection="…"] [name="…"]',
        mode: "ms",
        summary:
          "Sets the plan reflection and the signing name. Defaults to a canned reflection and the climber's own name.",
        examples: [
          'plan-sign reflection="It builds on how much I love being outdoors" name="Sam Lee"',
        ],
      },
    ],
  },
];

export interface ManifestExample {
  id: string;
  title: string;
  description: string;
  manifest: string;
}

/**
 * Whole manifests, ready to paste. Ids are real for hat 1923 (Conservation
 * Scientist); program= and school= are only valid in the district named.
 */
export const MANIFEST_EXAMPLES: ManifestExample[] = [
  {
    id: "ms-climb-plan-revision-loop",
    title: "MS climber with an approved Climb Plan, sent back once",
    description:
      "Completes the grade 7 Core assessment, picks Top Hats, then drafts a plan with every section set, submits it, gets revisions requested, edits three keys and resubmits, and is approved. The other sections carry over on resubmit. program= and school= are ids in Marc's test district (dis-6c07073b); in another district, look them up below.",
    manifest: `mode ms
product ms-base

# === Core Personality (RIASEC) ===
core-values grade=7
facet 3519 44/48 RIASEC:R
facet 3519 40/48 RIASEC:I
facet 3519 20/48 RIASEC:A
facet 3519 28/48 RIASEC:S
facet 3519 12/48 RIASEC:E
facet 3519 16/48 RIASEC:C

set-top-hat 1923 1994 3252
building-block activity=89 description="Built a line-following robot with my team" duration="1 - 2 years"

# Draft the plan, every section set explicitly
climb-plan drafted hat=1923
plan-profile source=core-values reflection="Being outside and protecting animals matters most to me"
plan-high-school program=891 school=529 goal=2877 goal-reflection="I want to volunteer at the nature centre"
plan-skills best=279 improve=643 excited=2730 reflection="I want to get better at planning my projects"
plan-after paths=34
plan-sign reflection="It builds on how much I love being outdoors" name="Sam Lee"
climb-plan submitted

# Coach sends it back; climber changes program, goal and one skill, then resubmits
climb-plan revisions-requested comment="Which club could you join?" coach="Ms. Rivera"
plan-high-school program=890 goal=2878 goal-reflection="I could join the Environmental Club at Cedar Hollow"
plan-skills improve=232
climb-plan submitted

climb-plan approved comment="Great plan!" coach="Ms. Rivera"`,
  },
  {
    id: "ms-climb-plan-building-blocks",
    title:
      "MS climber with a building-blocks profile and several education paths",
    description:
      "Completes the grade 7 Core assessment, logs three building blocks, then plans for Water Treatment Operator (3252). The profile is connected to two of the blocks (blocks= takes the activity= ids of building-block lines earlier in the file, up to three). The plan picks Explore Credentials (2875) as its Key to Success and all three of the hat's education paths: Career / Technical School (38), Straight to Work (37) and Military Training (36). A plan holds one Key to Success; goal= takes one of the hat's three (2875, 2877, 2878). Skills take their defaults, and the plan is approved on the first try. With no district-specific ids, it works in any district.",
    manifest: `mode ms
product ms-base

# === Core Personality (RIASEC) ===
core-values grade=7
facet 3519 44/48 RIASEC:R
facet 3519 40/48 RIASEC:I
facet 3519 20/48 RIASEC:A
facet 3519 28/48 RIASEC:S
facet 3519 12/48 RIASEC:E
facet 3519 16/48 RIASEC:C

set-top-hat 1923 1994 3252

# === Building blocks ===
building-block activity=101 description="I walk and feed my neighbour's two dogs every afternoon" duration="1 - 2 years"
building-block activity=104 description="I helped run a food drive at our community centre last winter" duration="< 1 year"
building-block activity=89 description="Built a line-following robot with my team" duration="1 - 2 years"

# Plan on Water Treatment Operator: profile from two of the blocks, a chosen
# Key to Success, and all three of the hat's education paths
climb-plan drafted hat=3252
plan-profile source=blocks blocks=101,89 reflection="Caring for animals and building things both show I like hands-on work"
plan-high-school goal=2875 goal-reflection="I want to earn a water operator certificate"
plan-after paths=38,37,36
climb-plan submitted

climb-plan approved comment="Nice connection to your dog walking!" coach="Ms. Rivera"`,
  },
];
